import * as Yup from 'yup';

import {
	positiveNumberValidator,
	simpleStringValidator,
} from '@common/react/utils/validationHelpers';
import { Nullable } from '@common/typescript/objects/Nullable';

import { stringValidator } from '@app/utilities/validators';
import { DeliveryType, ServiceType } from '@app/objects/Pet';
import { NEW_PET_ID, validationMessage } from '@app/components/Pages/ClinicPetEditor/ClinicApi';
import { CrematoryListInfo, getServiceTypePreferenceKind, useCrematoryServiceType } from '@app/objects/Crematory';
import { allowAdd } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/StoreEntriesModal/StoreEntriesModal';
import { DeliveryManager } from '@app/hooks/Delivery/useDeliveryManager';
import { DeliveryProvider } from '@app/components/Pages/PetEditor/Schemas';

const getValuesOfServiceType = () => {
	const values = new Set();
	const {
		useSemiPrivate,
		useCommunal,
		usePrivate,
		useStorage,
		useProductsOnly,
	} = useCrematoryServiceType();

	if (useCommunal) values.add(ServiceType.Communal);
	if (usePrivate) values.add(ServiceType.Private);
	if (useSemiPrivate) values.add(ServiceType.SemiPrivate);
	if (useStorage) values.add(ServiceType.Storage);
	if (useProductsOnly) values.add(ServiceType.ProductsOnly);

	const types = [...values] as Array<ServiceType>;

	return {
		values: types,
		names: types.map((i) => ServiceType[i]).join(', '),
	};
};

interface ValidationSchemaConfig {
	crematory?: CrematoryListInfo;

	manager: DeliveryManager;
	provider: DeliveryProvider;
}

function isAddressRequired(config: ValidationSchemaConfig, type: Nullable<DeliveryType>, id: Nullable<number | string>): boolean {
	const manager = config.manager;
	const provider = config.provider;
	const value = provider(type, id);

	return manager.isMail(value) || manager.isHomeDropOff(value) || manager.isVetDropOff(value);
}

export const getValidationSchema = (config: ValidationSchemaConfig) => Yup.object().shape({
	ownerLastName: simpleStringValidator,
	name: simpleStringValidator,
	petSpecieId: positiveNumberValidator,
	petBreedId: positiveNumberValidator,
	genderId: Yup.number().moreThan(NEW_PET_ID, validationMessage).required(validationMessage),
	reportedWeight: positiveNumberValidator,
	serviceType: Yup
		.number()
		.oneOf(getValuesOfServiceType().values, `Service Type must be one of the following values: ${getValuesOfServiceType().names}`)
		.required(validationMessage),
	discountId: Yup.number().positive('Must be positive').nullable(),
	noAshes: Yup.boolean()
		.when('serviceType', {
			is: (serviceType) => ServiceType.Communal === +serviceType,
			then: () => Yup.boolean().oneOf([true], 'Required field'),
		}),
	services: Yup.array().of(
		Yup.object().shape({
			count: Yup.number()
				.required('Quantity is required filed')
				.min(0, 'Quantity should be a positive value')
				.integer('Quantity quantity should be an integer value'),
		}),
	),
	urns: Yup.array().when('serviceType', {
		is: (val) => val !== ServiceType.Communal,
		then: () => Yup.array().of(
			Yup.object().shape({
				count: Yup.number().when('done', {
					is: (val) => val === false,
					then: () => Yup.number().min(1, 'Count should be a positive value'),
				})
					.required('Count should be a positive value')
					.integer('Count quantity should be an integer value'),
				id: Yup.number().when(['entry', 'nodeId'], {
					is: (entry, nodeId) => !allowAdd(entry, nodeId),
					then: () => Yup.number().max(-3, 'Not all parameters for the urn are selected'),
				}),
			}),
		),
	}),
	deliveryAddress: Yup
		.string()
		.when(['deliveryType', 'deliveryTypeId'], {
			is: (type, id) => isAddressRequired(config, type, id),
			then: () => stringValidator('Delivery address'),
		}),
	deliveryCity: Yup
		.string()
		.when(['deliveryType', 'deliveryTypeId'], {
			is: (type, id) => isAddressRequired(config, type, id),
			then: () => stringValidator('Delivery city'),
		}),
	deliveryStateId: Yup
		.number()
		.nullable()
		.when(['deliveryType', 'deliveryTypeId'], {
			is: (type, id) => isAddressRequired(config, type, id),
			then: () => Yup
				.number()
				.min(1, 'Delivery State is required')
				.required('Delivery State is required'),
		}),
	deliveryZip: Yup
		.string()
		.when(['deliveryType', 'deliveryTypeId'], {
			is: (type, id) => isAddressRequired(config, type, id),
			then: () => stringValidator('Delivery zip'),
		}),
	internalIdNum: Yup.string().when('serviceType', {
		is: (type) => config.crematory?.[getServiceTypePreferenceKind(type)]?.requireInternalId,
		then: () => stringValidator('Tag Id'),
	}),
});
